import React, { useRef } from "react";
import { Box } from "@mui/material";
import useCardShader from "./useCardShader";
import { prefersReducedMotion } from "../CivixButton/buttonTokens";

function parseHex(hex) {
  if (!hex || !hex.startsWith("#") || hex.length < 7) return [1, 1, 1];
  return [
    parseInt(hex.slice(1, 3), 16) / 255,
    parseInt(hex.slice(3, 5), 16) / 255,
    parseInt(hex.slice(5, 7), 16) / 255,
  ];
}

export default function CivixCard({
  children,
  variant = "glass", // "glass" | "outlined" | "elevated"
  sx = {},
  ...props
}) {
  const rootRef = useRef(null);
  const isGlass = variant === "glass";
  const reducedMotion = prefersReducedMotion();

  // Subtle webGL background colors
  const baseColor = parseHex("#f8fafc"); // Slate 50
  const highlightColor = parseHex("#059669"); // Emerald 600

  const { canvasRef, updatePointer, handleMouseLeave, active } = useCardShader({
    enabled: isGlass && !reducedMotion,
    rootRef,
    baseColor,
    highlightColor,
  });

  const handleMouseMove = (e) => {
    if (active) updatePointer(e.clientX, e.clientY);
    props.onMouseMove?.(e);
  };

  const handleLeave = (e) => {
    if (active) handleMouseLeave();
    props.onMouseLeave?.(e);
  };

  return (
    <Box
      ref={rootRef}
      onMouseMove={active ? handleMouseMove : props.onMouseMove}
      onMouseLeave={active ? handleLeave : props.onMouseLeave}
      {...props}
      sx={{
        position: "relative",
        borderRadius: "var(--radius-card, 16px)",
        p: { xs: 2.5, md: 3 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden", // clip canvas
        isolation: "isolate",
        transition: "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease",
        
        ...(isGlass
          ? {
              bgcolor: "transparent",
              border: "1px solid rgba(15, 23, 42, 0.08)",
              boxShadow: "0 8px 32px rgba(15, 23, 42, 0.04)",
              "&:hover": {
                boxShadow: "0 12px 48px rgba(4, 120, 87, 0.08)",
                borderColor: "rgba(4, 120, 87, 0.15)",
                transform: "translateY(-2px)",
              },
            }
          : variant === "elevated"
          ? {
              bgcolor: "var(--join-surface-elevated, #fff)",
              border: "1px solid transparent",
              boxShadow: "var(--join-shadow-elevated, 0 8px 24px rgba(15, 23, 42, 0.08))",
            }
          : {
              bgcolor: "var(--join-surface-elevated, #fff)",
              border: "1px solid var(--join-border-muted, #e4e4e4)",
              boxShadow: "var(--join-shadow-card, 0 2px 8px rgba(15, 23, 42, 0.06))",
            }),
        ...sx,
      }}
    >
      {active && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "block",
            }}
          />
        </Box>
      )}

      {isGlass && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)",
            backdropFilter: "blur(12px)",
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
      )}

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
